import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/dist/sweetalert2.css';
import {randomBeta} from 'd3-random';
import './styles/common.scss';
import './styles/pop-up-modal.scss';
import './styles/slide-in-box.scss';
import './styles/banner.scss';
import './styles/inline.scss';
import './styles/preview-modals.scss';

import {magnetIsAllowedByPatterns} from './rule';
import feature from './feature';
import {scopeCustomCss} from './customCss';
import countryCodes from './countryCodes';
import statesUS from './statesUS';
import provincesCanada from './provincesCanada';
import {isExpired, setExpirationAfter} from './time';

let magnet = null;

async function prepareSwalOptions(tracker, data, form_data, wrap) {
   let options;
   switch (form_data.prompt_type) {
      case 'inline':
         options = await prepareInlineWidgetOptions(data, form_data.prompt_css_selector, wrap, tracker.featureFlags[feature.Sc17569]);
         break;
      case 'slide_in_box':
         options = await prepareSlideInBoxWidgetOptions(data, form_data.prompt_position, wrap, tracker.featureFlags[feature.Sc17569]);
         break;
      case 'banner':
         options = await prepareBannerWidgetOptions(data, form_data.prompt_position, wrap);
         break;
      default:
         options = await prepareModalWidgetOptions(data, wrap);
   }

   if (!options) {
      return null;
   }

   options = await preparePromptActionPositionOptions(options, form_data.actions_position);

   return options;
}

async function preparePromptActionPositionOptions(options, position) {
   if (position?.length === 0) {
      return options;
   }
   const actions = `swal-actions-${position}`;
   options.customClass = {
      actions,
      ...options.customClass,
   };
   return options;
}

async function prepareModalWidgetOptions(data, wrap) {
   const target = document.body;
   if (target) {
      target.appendChild(wrap);
   }
   return {
      target: wrap,
      didOpen: () => {
         const popup = Swal.getPopup()
         popup.id = data.form_id;
         popup.focus();
         const countrySelectContainers = document.querySelectorAll('.country-select-container');
         countrySelectContainers.forEach(container => {
            const matchingStateContainer = findMatchingStateContainer(container);
            if (matchingStateContainer) {
               addListenerToCountrySelect(container, matchingStateContainer);
            }
         });
      },
   };
}

async function prepareInlineWidgetOptions(data, cssSelector, wrap, shouldUseCustomFormFields) {
   const target = document.querySelector(cssSelector);
   if (!target) {
      console.warn(`[hum] Inline campaign selector not found: "${cssSelector}"`);
      return null;
   }
   target.appendChild(wrap);
   return {
      target: wrap,
      template: '#my-template',
      toast: true,
      width: '100%',
      timer: 10e10,
      timerProgressBar: false,
      position: 'bottom',
      showClass: {
         popup: 'animate__fadeIn',
         backdrop: 'swal2-backdrop-hide',
      },
      showCloseButton: false,
      didOpen: () => {
         const popup = document.getElementsByClassName('swal2-toast')[0];
         popup.classList.remove('swal2-toast');
         popup.id = data.form_id;
         popup.removeAttribute('role');
         if (shouldUseCustomFormFields) {
            const countrySelectContainers = document.querySelectorAll('.country-select-container');
            countrySelectContainers.forEach(container => {
               const matchingStateContainer = findMatchingStateContainer(container);
               if (matchingStateContainer) {
                  addListenerToCountrySelect(container, matchingStateContainer);
               }
            });
         }
      },
   };
}

async function prepareSlideInBoxWidgetOptions(data, widgetPosition, wrap, shouldUseCustomFormFields) {
   const popupShowClass = widgetPosition.includes('start') ? 'animate__slideInLeft' : 'animate__slideInRight';
   const popupHideClass = widgetPosition.includes('start') ? 'animate__slideOutLeft' : 'animate__slideOutRight';

   const target = document.body;
   if (target) {
      target.appendChild(wrap);
   }
   return {
      target: wrap,
      template: '#my-template',
      toast: true,
      timer: 10e10,
      timerProgressBar: false,
      position: widgetPosition,
      showClass: {
         popup: `animate__animated ${popupShowClass}`,
      },
      hideClass: {
         popup: `animate__animated ${popupHideClass}`,
      },
      didOpen: () => {
            const popup = Swal.getPopup();
            popup.classList.remove('swal2-toast');
            popup.id = data.form_id;
            popup.role = 'dialog';
            popup.ariaLive = 'assertive';
            popup.ariaModal = 'true';

            // handle focus for the modal trying to repeat the logic from sweetalert
            // the library itself has a lot of restricted/unsupported features when toast type is enabled
            const focusableElements = popup.querySelectorAll(
               'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            let firstElement;
            let lastElement;
            let confirmButton;
            if (focusableElements.length > 0) {
               firstElement = focusableElements[0];
               lastElement = focusableElements[focusableElements.length - 1];
            } else {
               firstElement = popup;
               lastElement = popup;
            }
            popup.focus();

            // Trap focus inside the modal
            popup.addEventListener("keydown", function (event) {
               if (event.key === "Tab") {
                  if (event.shiftKey) {
                     if (document.activeElement === firstElement) {
                        event.preventDefault();
                        lastElement.focus();
                     }
                  } else {
                     if (document.activeElement === lastElement) {
                        event.preventDefault();
                        firstElement.focus();
                     }
                  }
               }
            });
         if (shouldUseCustomFormFields) {
            const countrySelectContainers = document.querySelectorAll('.country-select-container');
            countrySelectContainers.forEach(container => {
               const matchingStateContainer = findMatchingStateContainer(container);
               if (matchingStateContainer) {
                  addListenerToCountrySelect(container, matchingStateContainer);
               }
            });
         }
      },
   };
}

async function prepareBannerWidgetOptions(data, widgetPosition, wrap) {
   const popupShowClass = widgetPosition.includes('top') ? 'animate__slideInDown' : 'animate__slideInUp';
   const popupHideClass = widgetPosition.includes('top') ? 'animate__slideOutUp' : 'animate__slideOutDown';

   const target = document.body;
   if (target) {
      target.appendChild(wrap);
   }
   return {
      target: wrap,
      template: '#my-template',
      position: widgetPosition,
      grow: 'row',
      backdrop: false,
      showClass: {
         popup: `animate__animated ${popupShowClass}`,
      },
      hideClass: {
         popup: `animate__animated ${popupHideClass}`,
      },
      didOpen: () => {
         const popup = Swal.getPopup();
         popup.id = data.form_id;
         popup.removeAttribute('role');
         popup.ariaLive = 'polite';

         const countrySelectContainers = document.querySelectorAll('.country-select-container');
         countrySelectContainers.forEach(container => {
            const matchingStateContainer = findMatchingStateContainer(container);
            if (matchingStateContainer) {
               addListenerToCountrySelect(container, matchingStateContainer);
            }
         });
      }
   };
}

// We create a similar to real Campaign modal with different checks but not send any data to event endpoint if click on (submit/cancel/dismiss) btn.
export async function showPreviewMagnetCampaign(campaign, tracker) {
   if (campaign.form_data && Object.keys(campaign.form_data).length !== 0) {
      magnet = campaign;
      const previewInstitutionName = 'Hum';
      const title = findAndReplacePrimaryInstitutionName(campaign?.form_data?.title, previewInstitutionName);
      const body = setUpMagnetBody(campaign, previewInstitutionName, tracker);
      const footer = setUpMagnetFooter(campaign, previewInstitutionName, tracker);
      let data = {form_id: magnet.code};
      let hideModal = false;

      const submit_label = campaign.form_data.submit_label || 'Submit';

      const shouldUseCustomFormFields = tracker.featureFlags[feature.Sc17569];

      const conversionLinkTrackingEnabled = tracker.featureFlags[feature.Sc27297];
      const customCssEnabled = tracker.featureFlags[feature.Sc29425];

      let options;
      options = {
         html: body,
         preConfirm: magnetPreConfirm(data),
         willClose: () => hideModal = checkHideModal(),
         didRender: (doc) => magnetDidRender(doc, data, true, tracker.settings, conversionLinkTrackingEnabled),
         didOpen: () => {
            Swal.getPopup().id = campaign.code;
            if (shouldUseCustomFormFields) {
               const countrySelectContainers = document.querySelectorAll('.country-select-container');
               countrySelectContainers.forEach(container => {
                  const matchingStateContainer = findMatchingStateContainer(container);
                  if (matchingStateContainer) {
                     addListenerToCountrySelect(container, matchingStateContainer);
                  }
               });
            }
         },
         showConfirmButton: shouldShowConfirmButton(campaign, conversionLinkTrackingEnabled),
         confirmButtonText: submit_label,
         showCancelButton: campaign.form_data.cancel_toggle === undefined ? true : campaign.form_data.cancel_toggle,
         cancelButtonText: 'Cancel',
         showCloseButton: true,
         footer: footer,
         allowOutsideClick: false,
         inputAutoFocus: false,
         allowEnterKey: false,
      };

      const magnetWrap = document.createElement('div');
      magnetWrap.classList.add('swal-wrapper');
      magnetWrap.classList.add('hum-' + (magnet.form_data.prompt_type ?? 'modal'));
      let customCssStyleEl = null;
      if (customCssEnabled && typeof campaign.custom_css === 'string' && campaign.custom_css.trim() !== '') {
         magnetWrap.classList.add('hum-style-root');
         customCssStyleEl = injectCustomCss(magnetWrap, campaign.custom_css);
      }

      const preparedOptions = await prepareSwalOptions(tracker.settings, data, campaign.form_data, magnetWrap);
      if (!preparedOptions) {
         return;
      }
      options = {...options, ...preparedOptions};

      Swal.fire({
         ...options,
         didClose: () => {
            if (customCssStyleEl) {
               customCssStyleEl.remove();
            }
         },
      });
   }
}

function shouldShowConfirmButton(campaign, conversionLinkTrackingEnabled) {
   campaign.lead_gen_form = campaign.type === "lead_gen_form";

   if (!conversionLinkTrackingEnabled) {
      //if the feature flag is off, just use the old logic. Delete this whole if block when cleaning up the feature flag
      return campaign.lead_gen_form || campaign.form_data.call_to_action_toggle ? true : campaign.form_data.call_to_action_toggle;
   }

   if (campaign?.lead_gen_form) {
      let hideSubmit = campaign?.form_data?.hide_submit ?? false;
      return !hideSubmit
   }

   return campaign?.form_data?.call_to_action_toggle;
}

function setUpMagnetBody(campaign, primaryInstitutionName, tracker) {
   let body = '';

   if (campaign.form_data.body_html) {
      const body_html = findAndReplacePrimaryInstitutionName(campaign.form_data.body_html, primaryInstitutionName);
      body += `${body_html}`;
   }

   if (campaign.form_data.inputs) {
      campaign.form_data.inputs = campaign.form_data.inputs.sort((a, b) => {
         if (Object.hasOwn(a, 'order') && Object.hasOwn(b, 'order')) {
            return a.order - b.order;
         } else {
            return 0;
         }
      });
      body += '<div class="swal-input-container"><form>';

      if (tracker.featureFlags[feature.Sc17569]) {
         for (const [key, value] of Object.entries(campaign.form_data.inputs)) {
            if (!value.checked) continue;

            body += '<div class="swal-input-row">';

            if (value.type) {
               body = addCustomField(body, key, value);
            } else {
               body = addDefaultField(body, key, value);
            }

            if (value.required && value.type !== 'checkbox') {
               // Hide the required span for states/provinces until needed.
               const hideClass = (value.type === 'location' && value.slug.endsWith('state')) ? 'swal-hidden' : '';
               body += `<span class="swal-span-required ${hideClass}">Required *</span>`;
            }
            body += '</div>';

         }
      } else {
         for (const [key, value] of Object.entries(campaign.form_data.inputs)) {
            if (!value.checked) continue;

            body += '<div class="swal-input-row">';

            body = addDefaultField(body, key, value);

            if (value.required) {
               body += '<span class="swal-span-required">Required *</span>';
            }
            body += '</div>';

         }
      }

      body += '</form></div>';
   }

   return body;
}

function setUpMagnetFooter(campaign, primaryInstitutionName, tracker) {
   let footer = '<div>';

   if (campaign.form_data.footer_html) {
      const footer_html = findAndReplacePrimaryInstitutionName(campaign.form_data.footer_html, primaryInstitutionName);
      footer += `${footer_html}`;
   }

   if (campaign.form_data.hide_modal_display) {
      const hide_modal_text = campaign.form_data.hide_modal_text || "Don't show me this message again.";
      footer += `<div class="hide-modal-container">
            <input id="hide-modal-input" style="width: auto !important;" type="checkbox">&nbsp;&nbsp;
            <label for="hide-modal-input">${hide_modal_text}</label>
         </div>`;
   }
   footer += `</div>`;

   return footer;
}

function magnetPreConfirm(data) {
   return () => {
      let allRequiredFills = true;
      const elements = Array.from(document.querySelectorAll('[data-key]'));
      elements.forEach(el => {
         const docEl = document.getElementById(el.id);
         if (docEl) {
            docEl.style.border = '1px solid #d9d9d9';
            document.getElementById(`error-` + el.dataset.label)?.remove();
            if ((el.dataset.required === 'true' && docEl.value === '') ||
               (el.dataset.key === 'email' && !validateEmail(docEl.value)) ||
               (el.dataset.key === 'country' && docEl.value === 'Country')
            ) {
               allRequiredFills = false;
               docEl.style.border = '1px solid red';
            }
         }
      });
      if (!allRequiredFills) {
         return false;
      }
      elements.forEach(el => {
         const docEl = document.getElementById(el.id);
         if (docEl && docEl.value) {
            switch (el.type) {
               case 'checkbox':
               case 'radio':
                  if (el.checked) {
                     data[el.dataset.key] = docEl.value;
                  }
                  break;
               default:
                  data[el.dataset.key] = docEl.value;
            }
         }
      });
   };
}

function checkHideModal() {
   return document.getElementById('hide-modal-input')?.checked ?? false;
}

function magnetDidRender(magnetDoc, data, previewMode = false, settings, conversionLinkTrackingEnabled) {
   // .swal2-title should not be removed, only hidden
   const classesToSearch = [
      '.swal2-input', '.swal2-file', '.swal2-range', '.swal2-select', '.swal2-radio', '.swal2-checkbox',
      '.swal2-textarea', '.swal2-image', '.swal2-icon', '.swal2-progress-steps', '.swal2-cancel', '.swal2-deny',
      '.swal2-validation-message', '.swal2-timer-progress-bar', '.swal2-timer-progress-bar-container'
   ];
   const selectorString = classesToSearch.map(classSelector => `${classSelector}[style*="display: none"]`).join(',');

   const hiddenElements = magnetDoc.querySelectorAll(selectorString);
   hiddenElements.forEach(el => el?.remove());

   if (data.prompt_type === 'inline') {
      magnetDoc.removeAttribute('tabindex');
   }

   if (previewMode) {
      addPreviewTooltipToButtons(magnetDoc)
   }

   if (settings?.enableCodeSnippets === true) {
      cutAndRunScript(magnetDoc);
   }

   if (conversionLinkTrackingEnabled) {
      const conversionTargets = magnetDoc.querySelectorAll('[data-conversion-target]');
      conversionTargets.forEach(element => {
         element.addEventListener('click', () => {
            Swal.close();
         });
      });
   }
}

function cutAndRunScript(magnetDoc) {
   const htmlContainer = magnetDoc.querySelector('.swal2-html-container');
   const footer = magnetDoc.querySelector('.swal2-footer');

   if (htmlContainer) {
      htmlContainer.innerHTML = htmlContainer.innerHTML.replaceAll("&lt;", "<").replaceAll("&gt;", ">");
   }

   if (footer) {
      footer.innerHTML = footer.innerHTML.replaceAll("&lt;", "<").replaceAll("&gt;", ">");
   }

   const scripts = [];
   if (htmlContainer) {
      const htmlScripts = Array.from(htmlContainer.getElementsByTagName('script'));
      scripts.push(...htmlScripts);
   }
   if (footer) {
      const footerScripts = Array.from(footer.getElementsByTagName('script'));
      scripts.push(...footerScripts);
   }

   const loadAndExecuteScripts = (scripts) => {
      const promises = scripts.map(script => {
         return new Promise((resolve, reject) => {
            const scriptElement = document.createElement('script');
            if (script.src) {
               scriptElement.src = script.src;
               scriptElement.onload = resolve;
               scriptElement.onerror = reject;
            } else {
               resolve();
            }
            document.head.appendChild(scriptElement);
         });
      });
      return Promise.all(promises);
   };

   const executeScripts = (scripts) => {
      scripts.forEach(script => {
         if (!script.src) {
            try {
               eval(script.innerText);
            } catch (error) {
               console.error('Error executing script:', error);
            }
         }
      });
   };

   // Load scripts asynchronously and wait for all to be loaded
   loadAndExecuteScripts(scripts)
      .then(() => {
         // All scripts loaded, now execute their content
         executeScripts(scripts);
      })
      .catch(error => {
         console.error('Error loading or executing scripts:', error);
      });
}

function injectCustomCss(wrapper, css) {
   if (!wrapper || !css || typeof css !== 'string') {
      return null;
   }
   const scoped = scopeCustomCss(css);
   if (!scoped) {
      return null;
   }
   const styleEl = document.createElement('style');
   styleEl.setAttribute('data-hum-custom-css', 'true');
   styleEl.textContent = scoped;
   wrapper.appendChild(styleEl);
   return styleEl;
}

function addPreviewTooltipToButtons(doc) {
   let swalButtons = doc.querySelectorAll('button');

   swalButtons.forEach((swalButton) => {
      let tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'hum-tooltip hum-tooltip-top';
      tooltipDiv.innerHTML = "You're in preview mode, which is for visual demonstration only. Prompt buttons are not clickable.";

      let wrapDiv = document.createElement('div');
      wrapDiv.className = 'hum-tooltip-container';

      let clonedButton = swalButton.cloneNode(true);
      clonedButton.classList.add('hum-disabled');

      wrapDiv.appendChild(clonedButton);
      wrapDiv.appendChild(tooltipDiv);

      if (swalButton.classList.contains('swal2-close')) {
         let subWrap = document.createElement('div');
         subWrap.style.display = "flex";
         subWrap.style.justifyContent = "end";
         subWrap.appendChild(wrapDiv);

         swalButton.parentNode.replaceChild(subWrap, swalButton);
      } else {
         swalButton.parentNode.replaceChild(wrapDiv, swalButton);
      }
   });
}

function validateEmail(email) {
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   return emailRegex.test(email);
}

function findAndReplacePrimaryInstitutionName(text = '', primaryInstitutionName) {
   return text.replaceAll('%%primary_institution_name%%', primaryInstitutionName);
}

export async function showPreviewMagnetWarning() {
   const title = '<p class="swal2-warning-title">&#128075; Heads Up!';
   const body = `<p class="swal2-warning-desc">This page isn't included in the current active page rules. The prompt will not appear here when your campaign goes live.</p>`;
   const submit_label = 'Preview Anyway';

   const options = {
      icon: "warning",
      title: title,
      html: body,
      showConfirmButton: true,
      confirmButtonText: submit_label,
      showCancelButton: false,
      showCloseButton: true,
      allowOutsideClick: false,
   };

   let showAnyway;
   await Swal.fire(options).then((result) => {
      showAnyway = result.isConfirmed;
   });

   return showAnyway;
}

export async function verifyInlineCssSelector(css_selector) {
   const target = document.querySelector(css_selector);
   if (target) {
      return Promise.resolve();
   }

   const title = '<p class="swal2-warning-title">We couldn’t find your CSS Selector';
   const body = `<p class="swal2-warning-desc">The CSS selector you entered couldn’t be found on this page, so we’re unable to display the prompt. Please check the selector and try again.</p>`;
   const submit_label = 'Close Preview';

   const options = {
      icon: "error",
      title: title,
      html: body,
      showConfirmButton: true,
      confirmButtonText: submit_label,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      showCloseButton: true,
      allowOutsideClick: false,
   };

   return await Swal.fire(options);
}

function findMatchingStateContainer(countryContainer) {
   const key = countryContainer.getAttribute('data-key');

   let stateKey = 'state';
   if (key !== 'country') {
      stateKey = key.replace(new RegExp("country$", "ig"), "state");
   }

   return document.querySelector(`.state-select-container[data-key='${stateKey}']`);
}

function addListenerToCountrySelect(countryContainer, stateContainer) {
   countryContainer.addEventListener('change', function (event) {
      const selectedCountry = event.target.value;
      const stateSelectContainer = stateContainer;
      let placeholder = stateSelectContainer.getAttribute('data-placeholder');
      const isRequired = stateSelectContainer.getAttribute('data-isrequired');
      const inputRow = stateSelectContainer.parentElement.closest('.swal-input-row');

      if (selectedCountry === 'US' || selectedCountry === 'CA') {
         const selectedCountryRegions = selectedCountry === 'US' ? statesUS : provincesCanada;
         if (!placeholder) {
            placeholder = selectedCountry === 'US' ? 'U.S. States/Territories' : 'Canadian Provinces';
         }
         let stateSelectHTML = `<option selected disabled value="">${placeholder ?? ''}</option>`;
         selectedCountryRegions.map(({name, code}) => stateSelectHTML += `<option value="${code}">${name}</option>`);

         //make states/provinces visible and update required status
         stateSelectContainer.setAttribute('data-required', isRequired);
         stateSelectContainer.innerHTML = stateSelectHTML;
         Array.from(inputRow.children).forEach(child => {
            child.classList.remove('swal-hidden');
         });
      } else {
         //reset states/provinces visibility, values and requirements
         Array.from(inputRow.children).forEach(child => {
            child.classList.add('swal-hidden');
         });
         stateSelectContainer.value = '';
         stateSelectContainer.setAttribute('data-required', 'false');
      }
   });
}

function addCustomField(body, key, value) {
   const {label, required, slug, placeholder, type, select_options, is_primary} = value;
   let isCountry;

   switch (type) {
      case 'text':
      case 'email':
         body += `<label for="swal-input${key}" class="swal-input-label">${label}<input data-label="${label}" data-required="${required}" data-key="${slug}" id="swal-input${key}" class="swal2-input swal-input" type="${type}" placeholder="${placeholder ?? ''}"></label>`;
         break;
      case 'checkbox':
         body += `<label for="swal-input${key}" class="swal-input-label swal-checkbox-label"><input data-label="${label.replace(/(<([^>]+)>)/gi, "")}" data-required="${required}" data-key="${slug}" id="swal-input${key}" class="swal2-input swal-input" type="${type}">${label}</label>`;
         break;
      case 'select':
         body += `<label for="swal-select${key}" class="swal-input-label">${label}<select id="swal-select${key}" class="swal2-input swal-input swal2-select swal-select" data-required="${required}" data-key="${slug}">`;
         body += `<option selected disabled value="">${placeholder ?? ''}</option>`;
         select_options.map(({label, value}) => body += `<option value="${value}">${label ? label : value}</option>`);
         body += `</select></label>`;
         break;
      case 'location':
         isCountry = slug.endsWith('country');

         if (isCountry) {
            body += `<label for="swal-select${key}" class="swal-input-label">${label}<select id="swal-select${key}" class="swal2-input swal-input swal2-select swal-select country-select-container" data-required="${required}" data-key="${slug}">`;
            body += `<option selected disabled>${placeholder ?? ''}</option>`;
            countryCodes.map(({name, iso}) => body += `<option value="${iso}">${name}</option>`);
            body += `</select></label>`;
            break;
         }

         // States/provinces will be hidden and empty until needed. Construct parent element here since this is the only place where we fill the attributes.
         body += `<label for="swal-select${key}" class="swal-input-label swal-hidden">${label}<select id="swal-select${key}" class="swal2-input swal-input swal2-select swal-select state-select-container" data-key="${slug}" data-label="${label}" data-isrequired="${required}" data-placeholder="${placeholder ?? ''}"></select></label>`;
         break;
   }

   return body;
}

function addDefaultField(body, key, value) {
   const {label, required, slug} = value;

   switch (slug) {
      case 'country':
         body += `<label for="swal-select${key}" class="swal-input-label">${label}<select id="swal-select${key}" class="swal2-input swal-input swal2-select swal-select" data-required="${required}" data-key="${slug}">`;
         body += `<option selected disabled>${label}</option>`;
         countryCodes.map(({name, iso}) => body += `<option value="${iso}">${name}</option>`);
         body += `</select></label>`;
         break;
      default:
         body += `<label for="swal-input${key}" class="swal-input-label">${label}<input data-label="${label}" data-required="${required}" data-key="${slug}" id="swal-input${key}" class="swal2-input swal-input" type="${slug === 'email' ? 'email' : 'text'}" placeholder="${label}"></label>`;
   }

   return body;
}
